import { getQueryStringFromOptions, getResponse } from '@satellite/services/serviceUtilities';
import { cacheableGet } from '@satellite/services/httpClient';

export default {
  async createYardTask(params, options = {}, requestConfig = {}) {
    const response = await axios.post('/yard-task', params, requestConfig);

    return getResponse(response, options);
  },
  /**
   * @template TRequest - The type of the request data
   * @template TResponse - The type of the response data
   *
   * Get yard tasks grid with improved API contract
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetQueryStringOptions} [options.query] - Query string options (fields, joins)
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig<TRequest>} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching
   * @returns {Promise<TResponse>} The response data
   */
  async getYardTasksGrid({
    query = {},
    getResponseConfig = {},
    requestConfig = {},
    enableCache = false
  } = {}) {
    const queryString = getQueryStringFromOptions(query || {});
    const url = `/yard-task/grid${queryString ? `?${queryString}` : ''}`;

    const axiosResponse = await cacheableGet(url, {
      ...requestConfig,
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },

  /**
   * @template TRequest - The type of the request data
   * @template TResponse - The type of the response data
   *
   * Get yard task list with improved API contract
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetQueryStringOptions} [options.query] - Query string options (fields, joins)
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig<TRequest>} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching
   * @returns {Promise<TResponse>} The response data
   */
  async getYardTaskList({
    query = {},
    getResponseConfig = {},
    requestConfig = {},
    enableCache = false
  } = {}) {
    const queryString = getQueryStringFromOptions(query || {});
    const url = `/yard-task${queryString ? `?${queryString}` : ''}`;

    const axiosResponse = await cacheableGet(url, {
      ...requestConfig,
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },

  /**
   * Distinct check categories, asset containers, and asset owners referenced by yard tasks
   * in the given warehouse + createDateTime window (for board filter dropdowns).
   * @template TResponse
   * @param {Object} [options]
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig]
   * @param {import('axios').AxiosRequestConfig} [options.requestConfig] - params (warehouseIds, fromDate, toDate) + signal
   * @param {boolean} [options.enableCache]
   * @returns {Promise<TResponse>} The response data
   */
  async getYardTaskFilterOptions({
    getResponseConfig = {},
    requestConfig = {},
    enableCache = false
  } = {}) {
    const axiosResponse = await cacheableGet('/yard-task/filter-options', {
      ...requestConfig,
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },

  /**
   * @template TRequest - The type of the request data
   * @template TResponse - The type of the response data
   *
   * Search yard task list via POST body — use instead of getYardTaskList when relatedIds is large
   * @param {Object} [options] - Request options
   * @param {Object} [options.body] - Request body (relatedEntity, relatedIds, statuses, type, etc.)
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options
   * @param {import('axios').AxiosRequestConfig<TRequest>} [options.requestConfig] - Axios request config (signal, headers, etc.)
   * @returns {Promise<TResponse>} The response data
   */
  async searchYardTaskList({ body = {}, getResponseConfig = {}, requestConfig = {} } = {}) {
    const axiosResponse = await axios.post('/yard-task/search', body, requestConfig);
    return getResponse(axiosResponse, getResponseConfig);
  },

  /**
   * @template TRequest - The type of the request data
   * @template TResponse - The type of the response data
   *
   * Get yard task by ID with improved API contract
   * @param {string} taskId - Task ID
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetQueryStringOptions} [options.query] - Query string options (fields, joins)
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig<TRequest>} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching
   * @returns {Promise<TResponse>} The response data
   */
  async getYardTask(
    taskId,
    { getResponseConfig = {}, requestConfig = {}, enableCache = false } = {}
  ) {
    const url = `/yard-task/${taskId}`;

    const axiosResponse = await cacheableGet(url, {
      ...requestConfig,
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },

  /**
   * @template TRequest - The type of the request data
   * @template TResponse - The type of the response data
   *
   * Get yard task timeline with improved API contract
   * @param {string} taskId - Task ID
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig<TRequest>} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching
   * @returns {Promise<TResponse>} The response data
   */
  async getYardTaskTimeline(
    taskId,
    { getResponseConfig = {}, requestConfig = {}, enableCache = false } = {}
  ) {
    const url = `/yard-task/${taskId}/timeline`;

    const axiosResponse = await cacheableGet(url, {
      ...requestConfig,
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },

  async updateYardTask(taskId, params, options = {}, requestConfig = {}) {
    const response = await axios.patch(`/yard-task/${taskId}`, params, requestConfig);
    return getResponse(response, options);
  },

  /**
   * @template TRequest - The type of the request data
   * @template TResponse - The type of the response data
   *
   * Record a location-check outcome (e.g. CONFIRMED) for an asset container on a yard task check.
   * @param {Object} params
   * @param {string} params.warehouseId
   * @param {string} params.yardTaskCheckId
   * @param {string} params.assetContainerId
   * @param {string} params.outcome - {@link import('@nova/core').YardTaskCheckLocationOutcome}
   * @param {Object} [options]
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig]
   * @param {import('axios').AxiosRequestConfig<TRequest>} [options.requestConfig]
   * @returns {Promise<TResponse>}
   */
  async createCheckAssetContainerOutcome(params, options = {}, requestConfig = {}) {
    const response = await axios.post('/yard-task/check-asset-container', params, requestConfig);
    return getResponse(response, options);
  }
};
